/**
 * Comment pin overlay (F-51/F-52) — rendered INSIDE the transformed camera
 * layer (docs/04 §5: dot background, table, elements and pins share ONE
 * board-space transform, so layers can never misalign). Pins counter-scale
 * (scale(1/zoom), origin at the tip) to stay a constant screen size while
 * their anchor point tracks board space exactly.
 *
 * Every render resolves each thread's anchor to its CURRENT board position —
 * cell rect from the grid's published layout pass, element x/y (or a peer's
 * live grab rect), or the raw board point. Anchors are id-based, so pins
 * follow row moves, sorts, resizes and element drags automatically; there is
 * deliberately NO event wiring to break (docs/04 §3 — keep it that way).
 *
 * C4 degradation (docs/07): when an anchor target POSITIVELY no longer
 * exists, the thread is re-anchored to the board at its last resolved
 * position and flagged orphaned — one idempotent transaction; concurrent
 * clients racing it converge. Orphaned pins render dimmed with a broken-link
 * glyph. Resolved threads hide their pins unless "show resolved" is on
 * (F-53); the open thread's pin always shows.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "zustand";

import { safeSetPointerCapture, type CanvasStore } from "../../core/canvas.js";
import { colorForName, currentUser, initialsFor } from "../../core/identity.js";
import type { BoardSession } from "../../core/session.js";
import { withAlpha } from "../../core/color.js";
import { resolvedElementRects } from "../elements/contained.js";
import {
  anchorDropChanged,
  anchorTargetExists,
  anchorTargetGone,
  resolveAnchorPosition,
  resolvePinDropTarget,
  type BoardPoint,
  type PinDropTarget,
} from "./anchoring.js";
import { elementRectLookup, useCommentThreads, useGrabRects } from "./hooks.js";
import { pinWidthPx, threadFacepile } from "./facepile.js";
import { threadMentionsMe, useMentionDirectory } from "./mentions.js";
import type { CommentsUiStore } from "./store.js";

/** Unscaled pin box (screen px at zoom 1) — must match .comment-pin CSS. */
export const PIN_SIZE_PX = 26;

/** F-52b: pointer travel (SCREEN px) before a pin press becomes a drag. */
const PIN_DRAG_THRESHOLD_SCREEN_PX = 4;

/** An in-flight F-52b pin drag. */
interface PinDrag {
  threadId: string;
  pointerId: number;
  startClientX: number;
  startClientY: number;
  /** The pin's resolved board position at pointerdown — the drag's origin. */
  startPos: BoardPoint;
  /** False until the pointer passes the threshold: a click, not yet a drag. */
  moved: boolean;
  /** Where the pin is now (board units) — only meaningful once `moved`. */
  point: BoardPoint;
  /** The prospective target under `point`, highlighted while dragging. */
  target: PinDropTarget | null;
}

/** Broken-link glyph for orphaned pins (F-52 degradation). */
const iconUnlink = (
  <svg
    className="comment-pin-unlink"
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M4 2.2 5.2 1a1.9 1.9 0 0 1 2.7 2.7L6.7 4.9" />
    <path d="M6 7.8 4.8 9a1.9 1.9 0 0 1-2.7-2.7l1.2-1.2" />
    <path d="M3.4 1.2 3 0M1.2 3.4 0 3M6.6 8.8 7 10M8.8 6.6 10 7" strokeWidth="1" />
  </svg>
);

export interface CommentPinsLayerProps {
  session: BoardSession;
  canvas: CanvasStore;
  ui: CommentsUiStore;
}

export function CommentPinsLayer({ session, canvas, ui }: CommentPinsLayerProps) {
  const zoom = useStore(canvas, (s) => s.camera.zoom);
  const elementsVersion = useStore(session.store, (s) => s.elementsVersion);
  const frozen = useStore(session.store, (s) => s.frozen);
  const synced = useStore(session.store, (s) => s.synced);
  const layout = useStore(ui, (s) => s.gridLayout);
  const showResolved = useStore(ui, (s) => s.showResolved);
  const openThreadId = useStore(ui, (s) => s.openThreadId);
  const draft = useStore(ui, (s) => s.draft);

  const threads = useCommentThreads(session);
  const grabRects = useGrabRects(session);
  /** F-54: whose threads are addressed to the reader — the pin's tagged mark. */
  const directory = useMentionDirectory();
  const meId = currentUser().id;

  const getElementRect = useCallback(
    (elementId: string) => elementRectLookup(session, grabRects, layout)(elementId),
    [session, grabRects, layout],
  );

  /** threadId → resolved board position, for THIS render pass. */
  const positions = useMemo(() => {
    void elementsVersion; // element geometry lives outside React state
    const map = new Map<string, BoardPoint>();
    for (const thread of threads) {
      const pos = resolveAnchorPosition(thread.anchor, getElementRect, layout);
      if (pos !== null) map.set(thread.id, pos);
    }
    return map;
  }, [threads, layout, elementsVersion, getElementRect]);

  // Last successfully-resolved position per thread — the C4 degradation
  // coordinate ("re-anchor at the LAST RESOLVED position"). Recorded BEFORE
  // the degradation effect below, so when a target vanishes this render, the
  // map still holds the previous render's position.
  const lastResolvedRef = useRef(new Map<string, BoardPoint>());
  useEffect(() => {
    positions.forEach((pos, id) => lastResolvedRef.current.set(id, pos));
  }, [positions]);

  // C4 degradation + its inverse. Gated on `synced` (an un-synced doc proves
  // nothing about existence) and on the freeze (F-45: no doc writes while
  // frozen — another client, or this one after reconnect, acts instead).
  //
  //  - EMPTY-THREAD SWEEP: concurrent deletes of the last two messages merge
  //    to a message-less thread on every replica (no local delete saw length
  //    hit zero) — no UI path could remove it, so the first client observing
  //    it deletes it (idempotent shared-layer re-check).
  //  - RE-ATTACH (docs/04 §3, amended 2026-07-13): an orphaned thread whose
  //    ORIGINAL target id exists again (the deletion was undone) is restored
  //    to its recorded anchor — undoing a row/element delete brings its
  //    comments back with it, like Miro.
  //  - DEGRADE: target positively gone → reanchor to board at the last
  //    position resolved HERE + orphan. A thread whose position was never
  //    resolved by ANY client (target died before a layout pass saw them
  //    coexist — the creation race) has no coordinate to degrade to: it is
  //    flagged orphaned in place so the panel lists it and the popover's
  //    fallback placement can open it (F-52 accessibility, C4 "never
  //    silently eat the input").
  useEffect(() => {
    if (!synced || frozen) return;
    const hasElement = (id: string) => session.board.hasElement(id);
    for (const thread of threads) {
      if (thread.messages.length === 0) {
        session.deleteEmptyCommentThread(thread.id);
        continue;
      }
      if (thread.orphaned) {
        const original =
          thread.origAnchor ?? (thread.anchor.kind !== "board" ? thread.anchor : undefined);
        // POSITIVE existence required (anchorTargetExists, not
        // !anchorTargetGone): with no published grid layout a cell target is
        // merely unknown — re-attaching on "not provably gone" would falsely
        // restore every orphaned cell thread while the grid is unmounted.
        if (original !== undefined && anchorTargetExists(original, hasElement, layout)) {
          session.restoreCommentAnchor(thread.id);
          continue;
        }
      }
      if (thread.anchor.kind === "board") continue; // nothing (more) to lose
      if (!anchorTargetGone(thread.anchor, hasElement, layout)) continue;
      const last = lastResolvedRef.current.get(thread.id);
      if (last !== undefined) session.orphanCommentThread(thread.id, last);
      else if (!thread.orphaned) session.flagCommentThreadOrphaned(thread.id);
    }
  }, [session, threads, layout, elementsVersion, synced, frozen]);

  /* ---------------- F-52b: drag a pin to re-anchor its thread -------- */

  const [drag, setDrag] = useState<PinDrag | null>(null);
  /** Mirrors `drag` for the pointer handlers (they must not read stale state). */
  const dragRef = useRef<PinDrag | null>(null);
  const setDragState = useCallback((next: PinDrag | null) => {
    dragRef.current = next;
    setDrag(next);
  }, []);
  /**
   * Did the gesture that just ended actually move? `click` fires AFTER
   * `pointerup` has already cleared the drag, so the guard that keeps a drag
   * from opening the popover (F-52b) needs this one-shot flag. Armed false on
   * every pointerdown, so a swallowed click can never poison the next one.
   */
  const dragMovedRef = useRef(false);

  // The pin the user is dragging vanished under them (the thread was deleted
  // remotely, or its last message was): pointer capture died with the DOM node,
  // so no pointerup will ever arrive — drop the drag rather than leaving the
  // preview pin and target highlight painted on the board forever.
  useEffect(() => {
    const active = dragRef.current;
    if (active === null) return;
    if (!threads.some((t) => t.id === active.threadId)) setDragState(null);
  }, [threads, setDragState]);

  const beginPinDrag = useCallback(
    (event: React.PointerEvent, threadId: string, startPos: BoardPoint) => {
      dragMovedRef.current = false;
      if (event.button !== 0 || frozen) return; // F-45: no re-anchor while frozen
      safeSetPointerCapture(event.currentTarget as HTMLElement, event.pointerId);
      setDragState({
        threadId,
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startPos,
        moved: false,
        point: startPos,
        target: null,
      });
    },
    [frozen, setDragState],
  );

  const onPinPointerMove = useCallback(
    (event: React.PointerEvent) => {
      const active = dragRef.current;
      if (active === null || active.pointerId !== event.pointerId) return;
      const dxScreen = event.clientX - active.startClientX;
      const dyScreen = event.clientY - active.startClientY;
      if (
        !active.moved &&
        Math.abs(dxScreen) < PIN_DRAG_THRESHOLD_SCREEN_PX &&
        Math.abs(dyScreen) < PIN_DRAG_THRESHOLD_SCREEN_PX
      ) {
        return; // still a click (F-52b: a drag must not open the popover)
      }
      // Board delta from the pointer delta: the pin's TIP is its anchor point,
      // so the dragged point IS the hit-test point. Derived from the drag's
      // own origin rather than the container rect, so a pan mid-drag can't
      // teleport the pin.
      const point = {
        x: active.startPos.x + dxScreen / zoom,
        y: active.startPos.y + dyScreen / zoom,
      };
      // F-52b: re-run the SAME top-down hit-test as the F-50 drop on every
      // move, so the highlight always shows exactly what a release would pick.
      // Contained elements hit-test at their RESOLVED rect (cell + dx/dy).
      const target = resolvePinDropTarget(
        point,
        // The SAME rects the pins are positioned from (grab-aware): while a
        // peer drags an element, the hit-test must see where it is drawn, not
        // the geometry it will commit on release — otherwise the highlight
        // outlines empty canvas and the drop silently picks the cell beneath.
        resolvedElementRects(session.board.listElements(), layout, grabRects),
        layout,
      );
      setDragState({ ...active, moved: true, point, target });
    },
    [session, layout, zoom, setDragState, grabRects],
  );

  const endPinDrag = useCallback(
    (event: React.PointerEvent) => {
      const active = dragRef.current;
      if (active === null || active.pointerId !== event.pointerId) return;
      setDragState(null);
      dragMovedRef.current = active.moved; // consumed by the pin's onClick guard
      if (!active.moved || active.target === null) return; // a click — onClick opens it
      const thread = session.board.getThread(active.threadId);
      if (thread === undefined) return; // deleted mid-drag (C4: the delete wins)
      // "Dropping on the current target is a no-op" (F-52b) — no write, no
      // undo entry, no sync op. The thread and its messages are never touched
      // either way: this is ONE whole-object `anchor` write.
      if (!anchorDropChanged(thread.anchor, active.target.anchor)) return;
      session.setCommentThreadAnchor(active.threadId, active.target.anchor);
    },
    [session, setDragState],
  );

  /** The two-finger camera gesture stole the pin's pointer (setPointerCapture
   * on the canvas) — drop the drag with NO re-anchor commit. On a normal
   * release endPinDrag has already cleared the drag, so this is a no-op. */
  const onPinLostCapture = useCallback(
    (event: React.PointerEvent) => {
      const active = dragRef.current;
      if (active === null || active.pointerId !== event.pointerId) return;
      setDragState(null);
      dragMovedRef.current = active.moved; // still swallow the trailing click
    },
    [setDragState],
  );

  const inv = 1 / zoom;

  return (
    <div className="comment-pins-layer">
      {threads.map((thread) => {
        // An empty thread (concurrent-delete merge) is mid-sweep — never
        // render its "?" pin.
        if (thread.messages.length === 0) return null;
        // F-53: resolved pins hidden unless toggled on — but never hide the
        // pin of the thread whose popover is open (panel jump on a resolved
        // thread must show where it points).
        if (thread.resolved && !showResolved && thread.id !== openThreadId) return null;
        const dragging = drag !== null && drag.moved && drag.threadId === thread.id;
        // While dragging, the pin rides the pointer instead of its (still
        // unchanged) anchor — the doc is only written on release.
        const pos = dragging ? drag.point : positions.get(thread.id);
        if (pos === undefined) return null; // unresolvable this pass
        const author = thread.messages[0]?.authorName ?? "?";
        const replies = thread.messages.length - 1;
        // F-52c: everyone in the thread, not just whoever opened it — a pin
        // three people have argued in used to look exactly like an unanswered
        // one. The width comes from the SAME function the hit-test uses
        // (facepile.ts), so the painted tail is always clickable.
        const faces = threadFacepile(thread.messages);
        const tagged = threadMentionsMe(thread, directory, meId);
        return (
          <button
            key={thread.id}
            type="button"
            className={
              "comment-pin" +
              (thread.orphaned ? " orphaned" : "") +
              (thread.resolved ? " resolved" : "") +
              (thread.id === openThreadId ? " open" : "") +
              (tagged ? " tagged" : "") +
              (dragging ? " dragging" : "")
            }
            style={{
              left: pos.x,
              top: pos.y,
              width: pinWidthPx(faces, PIN_SIZE_PX),
              transform: `scale(${inv})`,
            }}
            data-tip={
              `${author}${replies > 0 ? ` +${replies}` : ""}` +
              (tagged ? " — mentions you" : "") +
              (thread.orphaned ? " — original location removed" : "") +
              (frozen ? "" : " — drag to move this comment")
            }
            aria-label={
              `Comment thread by ${author}` +
              (replies > 0 ? `, ${replies} ${replies === 1 ? "reply" : "replies"}` : "") +
              (tagged ? ", mentions you" : "")
            }
            onPointerDown={(e) => {
              // Never starts a canvas pan / grid selection.
              e.stopPropagation();
              // F-52b: a press may become a re-anchor drag. A plain click is
              // unaffected — beginPinDrag only arms it (see onClick).
              beginPinDrag(e, thread.id, pos);
            }}
            onPointerMove={onPinPointerMove}
            onPointerUp={endPinDrag}
            onPointerCancel={endPinDrag}
            onLostPointerCapture={onPinLostCapture}
            onClick={(e) => {
              e.stopPropagation();
              // F-52b: "dragging a pin must NOT open its popover". endPinDrag
              // has already cleared the drag by now, so the gesture's own
              // moved-ness is what decides — same click-vs-drag guard as the
              // link card's open action (ElementsLayer).
              if (dragMovedRef.current) {
                dragMovedRef.current = false;
                return;
              }
              ui.getState().openThread(thread.id);
            }}
          >
            {faces.shown.map((face) => (
              <span
                key={face.id}
                className="comment-pin-avatar"
                style={{ background: colorForName(face.name) }}
              >
                {initialsFor(face.name)}
              </span>
            ))}
            {faces.overflow > 0 && (
              <span className="comment-pin-more">+{faces.overflow}</span>
            )}
            {thread.orphaned && iconUnlink}
          </button>
        );
      })}
      {/* F-52b: highlight the prospective target so it is unambiguous what
          the comment will belong to on release — element outline in THIS
          user's color, cell tint, bare-canvas ghost marker. Board units,
          inside the camera layer, so it tracks pan/zoom with the target. */}
      {drag?.moved === true && drag.target !== null && (
        <DropTargetHighlight target={drag.target} color={session.user.color} inv={inv} />
      )}
      {draft !== null && (
        <div
          className="comment-pin draft"
          style={{ left: draft.position.x, top: draft.position.y, transform: `scale(${inv})` }}
          aria-hidden="true"
        >
          <span className="comment-pin-avatar" style={{ background: session.user.color }}>
            {initialsFor(session.user.name)}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * F-52b: the prospective re-anchor target, drawn in BOARD units inside the
 * camera layer (so it sits exactly on the target at any pan/zoom). One shape
 * per anchor kind, matching the three rungs of the docs/04 §3 hit-test:
 *
 *  - element → an OUTLINE in the dragging user's own color (the same visual
 *    language as the F-46 grab indicator: "this element is mine right now");
 *  - cell → a TINT filling the cell (an outline would be lost among the grid
 *    lines it sits on);
 *  - board → a GHOST marker at the drop point (there is no target rect to
 *    trace — the honest signal is "this lands on bare canvas, right here").
 *
 * Borders counter-scale (`inv`) so they stay hairlines at any zoom.
 */
function DropTargetHighlight({
  target,
  color,
  inv,
}: {
  target: PinDropTarget;
  color: string;
  inv: number;
}) {
  if (target.kind === "board") {
    const size = 18 * inv;
    return (
      <div
        className="pin-drop-ghost"
        style={{
          left: target.point.x - size / 2,
          top: target.point.y - size / 2,
          width: size,
          height: size,
          borderWidth: 2 * inv,
        }}
        aria-hidden="true"
      />
    );
  }
  const { rect } = target;
  return (
    <div
      className={`pin-drop-target pin-drop-target-${target.kind}`}
      style={{
        left: rect.x,
        top: rect.y,
        width: rect.w,
        height: rect.h,
        ...(target.kind === "element"
          ? { outline: `${2 * inv}px solid ${color}`, background: withAlpha(color, 0.1) }
          : { background: withAlpha(color, 0.18), borderWidth: 2 * inv, borderColor: color }),
      }}
      aria-hidden="true"
    />
  );
}
